/**
 * Maykey Hair Care — Global JavaScript
 * Handles Cart Drawer, Variant Selectors, Accordions, and Micro-interactions
 */

document.addEventListener('DOMContentLoaded', () => {
  initCartDrawer();
  initMobileNav();
  initAccordions();
  initHeroTabs();
  initHeroParallax();
  initShadeTabs();
  initScrollAnimations();
  initProductCardInteractions();
  initCollectionSort();
  initMobileHeroVideo();
});

/* ============================================================
   0. MOBILE NAVIGATION DRAWER
   ============================================================ */
function initMobileNav() {
  const overlay = document.getElementById('mobileNavOverlay');
  const toggleBtn = document.getElementById('mobileMenuToggle');
  const closeBtn = document.getElementById('mobileNavClose');
  const accordionTrigger = document.querySelector('.mobile-nav-accordion-trigger');

  if (!overlay || !toggleBtn) return;

  function openMenu() {
    overlay.classList.add('open');
    toggleBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeMenu() {
    overlay.classList.remove('open');
    toggleBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  toggleBtn.addEventListener('click', openMenu);
  if (closeBtn) closeBtn.addEventListener('click', closeMenu);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeMenu();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('open')) {
      closeMenu();
    }
  });

  // Mobile submenu accordion
  if (accordionTrigger) {
    accordionTrigger.addEventListener('click', () => {
      const parent = accordionTrigger.closest('.mobile-nav-accordion');
      const isExpanded = accordionTrigger.getAttribute('aria-expanded') === 'true';
      accordionTrigger.setAttribute('aria-expanded', !isExpanded);
      parent.classList.toggle('open');
    });
  }
}

/* ============================================================
   1. CART DRAWER & REAL-TIME AJAX CART ENGINE
   ============================================================ */
function initCartDrawer() {
  const overlay = document.getElementById('cartDrawerOverlay');
  if (!overlay) return;

  window.openCartDrawer = () => {
    overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  window.closeCartDrawer = () => {
    overlay.classList.remove('open');
    document.body.style.overflow = '';
  };

  // Delegated Click Handlers for Drawer Triggers, Close, Overlay backdrop, and Drawer items
  document.addEventListener('click', async (e) => {
    // 1. Open Triggers
    const trigger = e.target.closest('[data-cart-drawer-trigger]');
    if (trigger) {
      e.preventDefault();
      window.openCartDrawer();
      return;
    }

    // 2. Close Buttons
    const closeBtn = e.target.closest('#cartDrawerCloseBtn, [data-cart-drawer-close]');
    if (closeBtn) {
      e.preventDefault();
      window.closeCartDrawer();
      return;
    }

    // 3. Backdrop Click (clicking outside drawer)
    if (e.target === overlay) {
      window.closeCartDrawer();
      return;
    }

    // 4. Cart Item 1-Click Remove Button
    const removeBtn = e.target.closest('.cart-item-remove-btn');
    if (removeBtn) {
      e.preventDefault();
      const lineKey = removeBtn.dataset.key;
      if (lineKey) {
        removeBtn.style.pointerEvents = 'none';
        const cartItem = removeBtn.closest('.cart-item');
        if (cartItem) cartItem.style.opacity = '0.4';
        await updateCartQuantity(lineKey, 0);
      }
      return;
    }

    // 5. Quantity Plus/Minus Buttons
    const qtyBtn = e.target.closest('.qty-btn');
    if (qtyBtn) {
      e.preventDefault();
      const lineKey = qtyBtn.dataset.key;
      const cartItem = qtyBtn.closest('.cart-item');
      const input = cartItem?.querySelector('.qty-input');
      if (!input || !lineKey) return;

      let currentQty = parseInt(input.value, 10) || 0;
      const action = qtyBtn.dataset.action;

      if (action === 'increase') currentQty += 1;
      if (action === 'decrease') currentQty = Math.max(0, currentQty - 1);

      input.value = currentQty;
      qtyBtn.style.pointerEvents = 'none';
      try {
        await updateCartQuantity(lineKey, currentQty);
      } finally {
        qtyBtn.style.pointerEvents = '';
      }
      return;
    }

    // 6. In-Drawer Upsell Add Button
    const upsellBtn = e.target.closest('.btn-cart-upsell-add');
    if (upsellBtn) {
      e.preventDefault();
      const variantId = upsellBtn.dataset.upsellVariantId;
      if (!variantId) return;

      upsellBtn.disabled = true;
      const origText = upsellBtn.innerHTML;
      upsellBtn.innerHTML = '<span>Adding...</span>';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        const data = await res.json();
        if (!res.ok) {
          const errText = data.description || data.message || 'Could not add item to bag.';
          showToast(errText, 'error');
          return;
        }
        await refreshCartDrawer();
      } catch (err) {
        console.error('Upsell error:', err);
        showToast('Connection error. Please try again.', 'error');
      } finally {
        upsellBtn.disabled = false;
        upsellBtn.innerHTML = origText;
      }
      return;
    }
  });

  // Delegated Change Handlers for Direct Quantity Input & Order Notes
  document.addEventListener('change', async (e) => {
    const input = e.target.closest('.qty-input');
    if (input) {
      const lineKey = input.dataset.key;
      const qty = parseInt(input.value, 10);
      if (lineKey && !isNaN(qty)) {
        await updateCartQuantity(lineKey, Math.max(0, qty));
      }
      return;
    }

    const noteArea = e.target.closest('#CartDrawer-Note');
    if (noteArea) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ note: noteArea.value })
        });
      } catch (err) {
        console.error('Note update failed:', err);
      }
    }
  });

  // Keyboard Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay.classList.contains('open')) {
      window.closeCartDrawer();
    }
  });

  // Intercept Ajax Add To Cart Forms
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;

    e.preventDefault();
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.innerHTML : '';

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
    }

    try {
      const formData = new FormData(form);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      const data = await res.json();
      if (!res.ok) {
        const errorMsg = data.description || data.message || 'Could not add item to bag. Please try again.';
        showToast(errorMsg, 'error');
        return;
      }

      await refreshCartDrawer();
      window.openCartDrawer();
    } catch (err) {
      console.error('Error adding to cart:', err);
      if (!navigator.onLine) {
        showToast('You appear to be offline. Please check your internet connection.', 'error');
      } else {
        form.submit();
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('loading');
        submitBtn.innerHTML = originalText;
      }
    }
  });
}

async function updateCartQuantity(lineKey, quantity) {
  const drawerBody = document.getElementById('cartDrawerBody');
  if (drawerBody) drawerBody.style.opacity = '0.5';

  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    });

    const data = await res.json();
    if (!res.ok) {
      const errorMsg = data.description || data.message || 'Maximum available quantity reached.';
      showToast(errorMsg, 'error');
      await refreshCartDrawer();
      return;
    }
    await refreshCartDrawer();
  } catch (err) {
    console.error('Error updating cart line:', err);
    showToast('Could not update quantity. Please check your connection.', 'error');
  } finally {
    if (drawerBody) drawerBody.style.opacity = '';
  }
}

function formatMoney(cents) {
  const num = (cents / 100);
  const formatted = num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  return `Rs. ${formatted}`;
}

async function refreshCartDrawer() {
  try {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();

    // 1. Update Cart Count Badges
    document.querySelectorAll('.header-cart-badge, #cartDrawerCount').forEach((badge) => {
      badge.textContent = badge.id === 'cartDrawerCount' ? `(${cart.item_count})` : cart.item_count;
    });

    // 2. Update Subtotal Amount
    const subtotalEl = document.getElementById('cartDrawerSubtotal');
    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price);
    }

    // 3. Update Free Delivery Progress Bar
    const shippingBar = document.getElementById('cartFreeShippingBar');
    if (shippingBar) {
      const threshold = parseInt(shippingBar.dataset.threshold, 10) || 250000;
      const remaining = threshold - cart.total_price;
      const progressPct = Math.min(100, Math.max(0, Math.round((cart.total_price / threshold) * 100)));

      if (remaining <= 0 && cart.item_count > 0) {
        shippingBar.innerHTML = `
          <div class="free-shipping-msg qualified">
            <svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
            <span><strong>🎉 Congratulations! You unlocked Free Delivery!</strong></span>
          </div>
          <div class="progress-track">
            <div class="progress-fill" style="width: 100%;"></div>
          </div>
        `;
      } else {
        shippingBar.innerHTML = `
          <div class="free-shipping-msg">
            <span>Add <strong id="cartFreeShippingRemaining">${formatMoney(Math.max(0, remaining))}</strong> more to unlock <strong>Free Delivery</strong> 🚚</span>
          </div>
          <div class="progress-track" role="progressbar" aria-valuenow="${progressPct}" aria-valuemin="0" aria-valuemax="100">
            <div class="progress-fill" id="cartFreeShippingFill" style="width: ${progressPct}%;"></div>
          </div>
        `;
      }
    }

    // 4. Update Cart Body Items & Empty State
    const body = document.getElementById('cartDrawerBody');
    const foot = document.getElementById('cartDrawerFoot');

    if (cart.item_count === 0) {
      if (foot) foot.style.display = 'none';
      if (body) {
        body.innerHTML = `
          <div class="cart-empty-state" id="cartEmptyState">
            <div class="cart-empty-icon">
              <svg class="icon" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            </div>
            <p class="cart-empty-title">Your shopping bag is empty</p>
            <p class="cart-empty-subtext">Discover our 15-minute botanical hair darkening formulas.</p>
            <a href="/collections/all" class="btn-primary cart-empty-btn">
              <span>Explore All Products</span>
              <svg class="icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            </a>
          </div>
        `;
      }
    } else {
      if (foot) foot.style.display = 'block';

      let itemsHtml = '<form action="/cart" method="post" id="cartDrawerForm" class="cart-items-list">';
      let hasBottle = false;
      let hasSachet = false;

      cart.items.forEach((item, index) => {
        if (item.title.includes('250ml')) hasBottle = true;
        if (item.title.includes('30ml')) hasSachet = true;

        const imgHtml = item.image
          ? `<img src="${item.image}" alt="${item.title}" class="cart-item-img" width="72" height="72" loading="lazy">`
          : `<div class="cart-item-placeholder"><svg class="icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M7 2h10v3H7z"/><path d="M6 5h12v17H6z"/></svg></div>`;

        const variantPill = (item.variant_title && item.variant_title !== 'Default Title')
          ? `<div class="cart-item-variant"><span class="cart-item-variant-pill">${item.variant_title}</span></div>`
          : '';

        itemsHtml += `
          <div class="cart-item" data-line="${index + 1}" data-key="${item.key}">
            <div class="cart-item-img-wrap">
              ${imgHtml}
            </div>
            <div class="cart-item-info">
              <div class="cart-item-head-row">
                <a href="${item.url}" class="cart-item-name">${item.product_title}</a>
                <button type="button" class="cart-item-remove-btn" data-action="remove" data-key="${item.key}" aria-label="Remove ${item.title}" title="Remove item">
                  <svg class="icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>
              ${variantPill}
              <div class="cart-item-pricing-row">
                <div class="cart-qty-selector">
                  <button type="button" class="qty-btn minus" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">–</button>
                  <input type="number" name="updates[]" value="${item.quantity}" min="0" class="qty-input" data-key="${item.key}" aria-label="Item quantity">
                  <button type="button" class="qty-btn plus" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
                </div>
                <span class="cart-item-price">${formatMoney(item.final_line_price)}</span>
              </div>
            </div>
          </div>
        `;
      });
      itemsHtml += '</form>';

      // Upsell Card
      let upsellHtml = '';
      if (hasBottle && !hasSachet) {
        upsellHtml = `
          <div class="cart-upsell-card" id="cartUpsellCard">
            <div class="cart-upsell-header">
              <span class="cart-upsell-tag">✨ Recommended Addition</span>
            </div>
            <div class="cart-upsell-inner">
              <div class="cart-upsell-img-wrap">
                <img src="/cdn/shop/files/MHC30BK-nobg.png?v=1787866989&width=140" alt="Maykey Hair Darkening Shampoo 30ml" width="56" height="56" loading="lazy">
              </div>
              <div class="cart-upsell-info">
                <strong class="cart-upsell-title">Maykey 30ml Sachet Pack</strong>
                <span class="cart-upsell-sub">Travel Sachet Pack • Easy Trial</span>
                <span class="cart-upsell-price">Rs. 499</span>
              </div>
              <button type="button" class="btn-cart-upsell-add" data-upsell-variant-id="49982158504178" aria-label="Add Maykey 30ml Sachet Pack to cart">
                <span>+ Add</span>
              </button>
            </div>
          </div>
        `;
      } else if (hasSachet && !hasBottle) {
        upsellHtml = `
          <div class="cart-upsell-card" id="cartUpsellCard">
            <div class="cart-upsell-header">
              <span class="cart-upsell-tag">✨ Recommended Addition</span>
            </div>
            <div class="cart-upsell-inner">
              <div class="cart-upsell-img-wrap">
                <img src="/cdn/shop/files/MHC_250_DB-nobg.png?v=1787862938&width=140" alt="Maykey Hair Darkening Shampoo 250ml" width="56" height="56" loading="lazy">
              </div>
              <div class="cart-upsell-info">
                <strong class="cart-upsell-title">Maykey 250ml Family Bottle</strong>
                <span class="cart-upsell-sub">Full Size 250ml • Best Value</span>
                <span class="cart-upsell-price">Rs. 2,499</span>
              </div>
              <button type="button" class="btn-cart-upsell-add" data-upsell-variant-id="49981286809842" aria-label="Add Maykey 250ml Bottle to cart">
                <span>+ Add</span>
              </button>
            </div>
          </div>
        `;
      }

      // Order Note
      const noteHtml = `
        <div class="cart-note-box">
          <details class="cart-note-details">
            <summary class="cart-note-summary">
              <span>📝 Add delivery instructions (optional)</span>
              <svg class="icon" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </summary>
            <div class="cart-note-content">
              <textarea id="CartDrawer-Note" name="note" class="cart-note-textarea" placeholder="e.g. Call before delivery, gate code, leave with guard..." aria-label="Delivery instructions">${cart.note || ''}</textarea>
            </div>
          </details>
        </div>
      `;

      if (body) {
        body.innerHTML = itemsHtml + upsellHtml + noteHtml;
      }
    }
  } catch (err) {
    console.error('Cart refresh error:', err);
  }
}

/* ============================================================
   2. ACCORDION DRAWERS (FAQ & PDP METAFIELDS)
   ============================================================ */
function initAccordions() {
  document.addEventListener('click', (e) => {
    // FAQ Trigger
    const faqBtn = e.target.closest('.faq-question-btn');
    if (faqBtn) {
      const item = faqBtn.closest('.faq-item');
      const isOpen = item.classList.contains('open');

      // Optional: close other FAQ items
      document.querySelectorAll('.faq-item.open').forEach((el) => {
        el.classList.remove('open');
        el.querySelector('.faq-question-btn')?.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('open');
        faqBtn.setAttribute('aria-expanded', 'true');
      }
      return;
    }

    // PDP Metafield Drawer Trigger
    const drawerBtn = e.target.closest('.pdp-drawer-trigger');
    if (drawerBtn) {
      const item = drawerBtn.closest('.pdp-drawer-item');
      const isOpen = item.classList.contains('open');

      document.querySelectorAll('.pdp-drawer-item.open').forEach((el) => {
        el.classList.remove('open');
        el.querySelector('.pdp-drawer-trigger')?.setAttribute('aria-expanded', 'false');
      });

      if (!isOpen) {
        item.classList.add('open');
        drawerBtn.setAttribute('aria-expanded', 'true');
      }
    }
  });
}

/* ============================================================
   3. HERO PRODUCT SWITCHER TABS
   ============================================================ */
function initHeroTabs() {
  const tabs = document.querySelectorAll('.switcher-tab-btn');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      tabs.forEach((t) => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');

      const target = tab.dataset.target;
      document.querySelectorAll('.hero-tab-content').forEach((content) => {
        content.style.display = content.id === target ? 'flex' : 'none';
      });
    });
  });
}

/* ============================================================
   4. SHADE SELECTOR TABS
   ============================================================ */
function initShadeTabs() {
  const tabButtons = document.querySelectorAll('.shade-tab-pill');
  const shadeCards = document.querySelectorAll('.shade-card');

  tabButtons.forEach((btn, index) => {
    btn.addEventListener('click', () => {
      tabButtons.forEach((b) => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      shadeCards.forEach((card, i) => {
        card.classList.toggle('active', i === index);
      });
    });
  });
}

/* ============================================================
   5. SCROLL TRIGGERED FADE-IN ANIMATIONS
   ============================================================ */
function initScrollAnimations() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  const elements = document.querySelectorAll(
    '.comparison-card, .shade-card, .ingredient-card, .step-card, .trust-card, .product-card'
  );

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );

  elements.forEach((el) => {
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition = 'opacity 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
    observer.observe(el);
  });
}

/* ============================================================
   6. HERO 3D CURSOR PARALLAX INTERACTION
   ============================================================ */
function initHeroParallax() {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return;

  const mediaWrap = document.querySelector('.hero-media-wrap');
  if (!mediaWrap) return;

  const images = mediaWrap.querySelectorAll('.hero-media-img, .product-card-placeholder');
  let rafId = null;

  mediaWrap.addEventListener('mousemove', (e) => {
    const rect = mediaWrap.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const xNorm = (x / rect.width - 0.5) * 2;
    const yNorm = (y / rect.height - 0.5) * 2;

    const rotX = -yNorm * 5;
    const rotY = xNorm * 7;

    if (rafId) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      images.forEach((img) => {
        img.style.transform = `perspective(1000px) rotateX(${rotX}deg) rotateY(${rotY}deg) translateY(-8px) scale(1.02)`;
      });
    });
  });

  mediaWrap.addEventListener('mouseleave', () => {
    if (rafId) cancelAnimationFrame(rafId);
    images.forEach((img) => {
      img.style.transform = '';
    });
  });
}

/* ============================================================
   7. PRODUCT CARD HOVER VIDEO & INTERACTIVE SWATCH PREVIEW
   ============================================================ */
function initProductCardInteractions() {
  const cards = document.querySelectorAll('.product-card');

  cards.forEach((card) => {
    const video = card.querySelector('.product-card-video');
    const primaryImg = card.querySelector('.product-card-img-primary');
    const swatches = card.querySelectorAll('.card-swatch-dot');

    // Video hover playback
    if (video) {
      card.addEventListener('mouseenter', () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            // Autoplay prevented, fail gracefully
          });
        }
      });

      card.addEventListener('mouseleave', () => {
        video.pause();
        video.currentTime = 0;
      });
    }

    // Interactive shade swatch preview
    if (swatches.length > 0 && primaryImg) {
      const originalSrc = primaryImg.src;

      swatches.forEach((swatch) => {
        swatch.addEventListener('mouseenter', () => {
          const variantImg = swatch.getAttribute('data-variant-img');
          if (variantImg) {
            primaryImg.src = variantImg;
          }
        });

        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const variantId = swatch.getAttribute('data-variant-id');
          const cardLink = card.querySelector('.product-card-link');
          if (cardLink && variantId) {
            const baseUrl = cardLink.getAttribute('href').split('?')[0];
            window.location.href = `${baseUrl}?variant=${variantId}`;
          }
        });
      });

      card.addEventListener('mouseleave', () => {
        primaryImg.src = originalSrc;
      });
    }
  });
}

/* ============================================================
   8. COLLECTION SORT DROPDOWN HANDLER
   ============================================================ */
function initCollectionSort() {
  const sortSelect = document.getElementById('CollectionSortBy');
  if (!sortSelect) return;

  sortSelect.addEventListener('change', (e) => {
    const value = e.target.value;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    window.location.href = url.toString();
  });
}

/* ============================================================
   9. MOBILE HERO AMBIENT VIDEO AUTOPLAY (iOS Safari Kickstart)
   ============================================================ */
function initMobileHeroVideo() {
  const heroVid = document.querySelector('.hero-bg-video');
  if (!heroVid) return;

  // Explicitly ensure video is muted and inline for iOS Safari & WebKit
  heroVid.muted = true;
  heroVid.defaultMuted = true;
  heroVid.setAttribute('playsinline', '');
  heroVid.setAttribute('webkit-playsinline', '');

  const startPlay = () => {
    heroVid.play().catch(() => {});
  };

  // Attempt initial playback immediately
  const playPromise = heroVid.play();
  if (playPromise !== undefined) {
    playPromise.catch(() => {
      // If iOS Low Power Mode blocks initial autoplay, start immediately on first interaction
      window.addEventListener('touchstart', startPlay, { passive: true, once: true });
      window.addEventListener('scroll', startPlay, { passive: true, once: true });
      window.addEventListener('click', startPlay, { passive: true, once: true });
    });
  }
}

/* ============================================================
   10. GLOBAL TOAST NOTIFICATION ENGINE
   ============================================================ */
function showToast(message, type = 'error') {
  let container = document.getElementById('maykeyToastContainer');
  if (!container) {
    container = document.createElement('div');
    container.id = 'maykeyToastContainer';
    container.className = 'maykey-toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('role', 'status');
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.className = `maykey-toast ${type}`;

  const iconSvg = type === 'error'
    ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>'
    : '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';

  toast.innerHTML = `
    <span class="toast-icon">${iconSvg}</span>
    <span class="toast-msg">${message}</span>
    <button type="button" class="toast-close" aria-label="Close notification">&times;</button>
  `;

  container.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('visible');
  });

  const dismiss = () => {
    toast.classList.remove('visible');
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 300);
  };

  const closeBtn = toast.querySelector('.toast-close');
  if (closeBtn) closeBtn.addEventListener('click', dismiss);

  setTimeout(dismiss, 4500);
}
window.showToast = showToast;



